import { onDocumentReady, prefersReducedMotion } from "@theme/utilities";
import { getIntersectionRoot, scrollContainerMediaQuery } from "@theme/scroll-container";
//#region assets/vp-entry-animations.ts
/**
* Subtle CSS entry animations for marketing and PDP sections.
*
* Markup:
*   <div data-vp-enter>                 — trigger group (observed)
*     <div data-vp-enter-item>...</div> — optional staggered children
*   </div>
*
* Optional attributes on the group:
*   data-vp-enter-eager  — play as soon as ready (hero / above-the-fold)
*   data-vp-enter-y      — start offset in px (default 0, opacity-only fade)
*   data-vp-enter-stagger — stagger in seconds (default 0.08)
*   data-vp-enter-overflow="visible" — skip overflow clip (e.g. kenteken overlap)
*
* Initial hide is applied via early CSS in snippets/scripts.liquid (html.vp-motion).
* Tweens run as CSS @keyframes — no GSAP on the critical path.
*
* Call `refreshVpEntryAnimations()` after DOM morphs that inject new groups.
*/
var GROUP_SELECTOR = "[data-vp-enter]";
var ITEM_SELECTOR = "[data-vp-enter-item]";
var ENTER_ANIMATION_NAME = "vp-enter-in";
var DEFAULT_Y = 0;
var DEFAULT_STAGGER = .08;
var FONTS_WAIT_MS = 600;
/** @type {IntersectionObserver | null} */
var observer = null;
/** @type {WeakSet<Element>} */
var played = /* @__PURE__ */ new WeakSet();
/** @type {Promise<void> | null} */
var fontsReadyPromise = null;
/**
* Collect stagger targets for a group, ignoring items nested in child groups.
* @param {Element} group
* @returns {Element[]}
*/
function getItems(group) {
	return Array.from(group.querySelectorAll(ITEM_SELECTOR)).filter((item) => {
		return item.closest(GROUP_SELECTOR) === group;
	});
}
/**
* @param {Element} group
* @returns {Element[]}
*/
function getTargets(group) {
	const items = getItems(group);
	return items.length > 0 ? items : [group];
}
/**
* @param {Element} group
* @returns {number}
*/
function getY(group) {
	return Number.parseFloat(group.getAttribute("data-vp-enter-y") || "") || DEFAULT_Y;
}
/**
* Wait until fonts settle (or timeout) so text metrics do not shift mid-tween.
* @returns {Promise<void>}
*/
function waitForFonts() {
	if (!document.fonts?.ready) return Promise.resolve();
	return Promise.race([document.fonts.ready.then(() => void 0), new Promise((resolve) => {
		setTimeout(resolve, FONTS_WAIT_MS);
	})]);
}
/**
* @returns {Promise<void>}
*/
function ensureFontsReady() {
	if (!fontsReadyPromise) fontsReadyPromise = waitForFonts();
	return fontsReadyPromise;
}
/**
* One frame so layout/paint have committed.
* @returns {Promise<void>}
*/
function waitForFrame() {
	return new Promise((resolve) => {
		requestAnimationFrame(() => resolve());
	});
}
/**
* @param {Element} group
*/
function primeGroup(group) {
	if (!(group instanceof HTMLElement)) return;
	const y = getY(group);
	group.style.setProperty("--vp-enter-y", `${y}px`);
}
/**
* @param {Element[]} targets
*/
function clearInlineMotionStyles(targets) {
	for (const target of targets) {
		if (!(target instanceof HTMLElement)) continue;
		target.style.removeProperty("--vp-enter-delay");
	}
}
/**
* @param {Element} group
* @param {Element[]} targets
*/
function finishGroup(group, targets) {
	group.setAttribute("data-vp-entered", "");
	group.classList.remove("is-vp-entering");
	clearInlineMotionStyles(targets);
}
/**
* @param {Element} group
*/
async function playGroup(group) {
	if (played.has(group)) return;
	await ensureFontsReady();
	if (played.has(group)) return;
	played.add(group);
	primeGroup(group);
	const targets = getTargets(group);
	const items = getItems(group);
	const stagger = Number.parseFloat(group.getAttribute("data-vp-enter-stagger") || "") || DEFAULT_STAGGER;
	targets.forEach((target, index) => {
		if (!(target instanceof HTMLElement)) return;
		const itemIndex = items.length > 0 ? items.indexOf(target) : index;
		const delay = Math.max(itemIndex, 0) * stagger;
		target.style.setProperty("--vp-enter-delay", `${delay}s`);
	});
	group.classList.add("is-vp-entering");
	let remaining = targets.length;
	const onAnimationEnd = (event) => {
		if (event.animationName !== ENTER_ANIMATION_NAME) return;
		if (!(event.target instanceof Element) || !targets.includes(event.target)) return;
		remaining -= 1;
		if (remaining > 0) return;
		for (const target of targets) target.removeEventListener("animationend", onAnimationEnd);
		finishGroup(group, targets);
	};
	for (const target of targets) target.addEventListener("animationend", onAnimationEnd);
}
/**
* @param {IntersectionObserverEntry[]} entries
*/
function onIntersect(entries) {
	for (const entry of entries) {
		if (!entry.isIntersecting) continue;
		const group = entry.target;
		observer?.unobserve(group);
		playGroup(group);
	}
}
function disconnectObserver() {
	observer?.disconnect();
	observer = null;
}
/**
* Above-the-fold groups — play after fonts settle (or timeout).
*/
function playEagerGroups() {
	document.querySelectorAll(`${GROUP_SELECTOR}[data-vp-enter-eager]`).forEach((group) => {
		playGroup(group);
	});
}
/**
* Observe remaining groups. CSS keeps them hidden until playGroup runs.
*/
function observeDeferredGroups() {
	disconnectObserver();
	observer = new IntersectionObserver(onIntersect, {
		root: getIntersectionRoot(),
		rootMargin: "0px 0px -8% 0px",
		threshold: .15
	});
	document.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
		if (played.has(group)) return;
		if (group.hasAttribute("data-vp-enter-eager")) {
			playGroup(group);
			return;
		}
		observer?.observe(group);
	});
}
function revealWithoutMotion() {
	document.documentElement.classList.remove("vp-motion");
	document.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
		getTargets(group).forEach((el) => {
			if (el instanceof HTMLElement) {
				el.style.opacity = "";
				el.style.visibility = "";
				el.style.transform = "";
				el.style.removeProperty("--vp-enter-delay");
			}
		});
		group.classList.remove("is-vp-entering");
		group.setAttribute("data-vp-entered", "");
		played.add(group);
	});
}
async function init() {
	if (prefersReducedMotion()) {
		revealWithoutMotion();
		return;
	}
	await waitForFrame();
	await ensureFontsReady();
	await waitForFrame();
	playEagerGroups();
	await waitForFrame();
	observeDeferredGroups();
	scrollContainerMediaQuery.addEventListener("change", () => {
		observeDeferredGroups();
	});
	window.addEventListener("vp:scroll-container-breakpoint", () => {
		observeDeferredGroups();
	});
}
onDocumentReady(init);
/**
* Re-scan the DOM for new `[data-vp-enter]` groups (e.g. after section morph).
*/
function refreshVpEntryAnimations() {
	if (prefersReducedMotion()) {
		revealWithoutMotion();
		return;
	}
	if (!observer) return;
	observeDeferredGroups();
}
//#endregion
export { refreshVpEntryAnimations };
